import { api } from '../client';

/**
 * The customer's view of stock held at their own sites.
 *
 * Deliberately narrower than the internal `consignment.api.ts`, and the
 * differences are the point rather than an omission: there is no unit cost, no
 * valuation and no capital figure anywhere in these shapes. What the supplier
 * paid for the material is the supplier's business.
 *
 * Nothing here takes a company or an owner. Every endpoint derives what the
 * caller may see from partnerships recorded against their own company.
 */

export interface PortalSummary {
  suppliers: string[];
  contractReferences?: (string | null)[];
  sites: number;
  skus: number;
  /** Held for them at their sites and not yet drawn — still the supplier's. */
  ourStockOnSite: number;
  /** Already theirs, title passed, not yet moved. */
  awaitingCollection: number;
  openCallOffs: number;
  /** Follows the partnership's access level, not a role the customer controls. */
  canRaiseCallOffs: boolean;
}

export interface PortalPositionLine {
  itemId: string;
  sku: string;
  name: string;
  unit: string | null;
  onSite: number;
  availableToDraw: number;
  yours: number;
  /** Only where somebody actually agreed one. Null is "none agreed", not zero. */
  agreedMin: number | null;
}

export interface PortalSite {
  id: string;
  name: string;
  code: string;
  lines: PortalPositionLine[];
}

export interface PortalCallOff {
  id: string;
  reference: string;
  status: string;
  retainedAtNode: boolean;
  requestedAt: string;
  decidedAt: string | null;
  fulfilledAt: string | null;
  collectedAt: string | null;
  notes: string | null;
  location: { id: string; name: string; code: string };
  lines: {
    id: string;
    quantityRequested: number;
    quantityFulfilled: number;
    item: { id: string; sku: string; name: string; unit: string | null };
  }[];
}

export interface PortalInbound {
  id: string;
  shipmentNumber: string;
  status: string;
  estimatedArrival: string | null;
  destination: { id: string; name: string; code: string };
  items: { quantity: number; item: { sku: string; name: string; unit: string | null } }[];
}

export interface PortalPerformance {
  supplier: string;
  contractReference: string | null;
  /** A share. Null means no target was agreed with you. */
  target: number | null;
  /** Null when no day of position has been recorded — an absence, not a failure. */
  availability: number | null;
  daysObserved: number;
  verdict: 'MET' | 'MISSED' | 'NO_TARGET' | 'NOT_MEASURED';
}

export const portalApi = {
  summary: async (): Promise<PortalSummary> => (await api.get<PortalSummary>('/portal/summary')).data,

  positions: async (): Promise<{ sites: PortalSite[] }> =>
    (await api.get<{ sites: PortalSite[] }>('/portal/positions')).data,

  callOffs: async (): Promise<PortalCallOff[]> =>
    (await api.get<PortalCallOff[]>('/portal/call-offs')).data,

  inbound: async (): Promise<PortalInbound[]> =>
    (await api.get<PortalInbound[]>('/portal/inbound')).data,

  performance: async (): Promise<PortalPerformance | null> =>
    (await api.get<PortalPerformance | null>('/portal/performance')).data,

  raiseCallOff: async (dto: {
    locationId: string;
    lines: { itemId: string; quantity: number }[];
    notes?: string;
  }): Promise<PortalCallOff> => (await api.post<PortalCallOff>('/portal/call-offs', dto)).data,
};
